import type { Actor, Issue, Label, ProjectColumn, StateSlug } from '../api/types'
import { keyId } from '../lib/keys'
import { BOARD_STATES, CLOSED_STATES, columnState, placeOf, priorityRank } from '../vocab'
import type { BoardView, LiveFilter, SortMode } from './view'

export const TERMINAL_WINDOW_DAYS = 14
export const normalizeText = (text: string) => text.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase()
export const searchTerms = (q: string) => normalizeText(q).split(/\s+/).filter(Boolean)
export const compareKeys = (a: string, b: string) => a < b ? -1 : a > b ? 1 : 0
export const compareManual = (a: Issue, b: Issue) => compareKeys(a.sort_key, b.sort_key) || a.number - b.number || compareKeys(a.id, b.id)
export function claimId(actor: Actor): string {
  return actor.kind === 'person' ? `person:${actor.member}` : actor.kind === 'agent' ? `session:${actor.session}` : 'poulpe'
}
export function matchesClaim(issue: Issue, claimed: string): boolean {
  if (!claimed) return true
  if (claimed === 'yes') return issue.claim !== null
  if (claimed === 'no') return issue.claim === null
  return issue.claim !== null && claimId(issue.claim.by) === claimed
}
export function resolveLabelIds(names: readonly string[], labels: ReadonlyMap<string, Label>): Set<string> {
  const wanted = new Set(names.map(n => n.toLowerCase()))
  return new Set([...labels.values()].filter(l => !l.deleted_at && wanted.has(l.name.toLowerCase())).map(l => l.id))
}
export interface FilterContext {
  labels: ReadonlyMap<string, Label>
  live?: Record<LiveFilter, ReadonlySet<string>>
  /** The keys the chosen plan lists, each as `keyId` reads it; null for no plan filter. */
  planKeys?: ReadonlySet<string> | null
}
export function filterIssues(issues: Iterable<Issue>, view: BoardView, ctx: FilterContext): Issue[] {
  const terms = searchTerms(view.q)
  const labels = resolveLabelIds(view.labels, ctx.labels)
  return [...issues].filter(issue => {
    if (issue.archived_at) return false
    if (view.live && !ctx.live?.[view.live].has(issue.id)) return false
    if (ctx.planKeys && !ctx.planKeys.has(keyId(issue.key) ?? '')) return false
    if (labels.size && !issue.labels.some(id => labels.has(id))) return false
    if (view.priorities.length && !view.priorities.includes(issue.priority)) return false
    if (!matchesClaim(issue, view.claimed)) return false
    // Both padded and unpadded keys match, as on today's board.
    const text = normalizeText([issue.key, issue.key.replace(/-0+(\d+)$/, '-$1'), issue.title, issue.description].join('\n'))
    return terms.every(t => text.includes(t))
  })
}
const comparators: Record<SortMode, (a: Issue, b: Issue) => number> = {
  manual: compareManual,
  priority: (a, b) => priorityRank(a.priority) - priorityRank(b.priority) || compareManual(a, b),
  updated: (a, b) => compareKeys(b.updated_at, a.updated_at) || compareManual(a, b),
  created: (a, b) => compareKeys(b.created_at, a.created_at) || compareManual(a, b),
}
export const sortIssues = (issues: Issue[], mode: SortMode) => issues.sort(comparators[mode])
export function inTerminalWindow(issue: Issue, now: number): boolean {
  const updated = Date.parse(issue.updated_at)
  return Number.isNaN(updated) || updated >= now - TERMINAL_WINDOW_DAYS * 86_400_000
}
export interface ColumnData {
  /** What the column is called to the board: its state's slug, or the id of the project's own column. */
  key: string
  /** The state its issues are in. */
  state: StateSlug
  /** The project's own column, none for the fixed column of a state. */
  column: ProjectColumn | null
  items: Issue[]
  total: number
  hidden: number
  terminal: boolean
  showAll: boolean
  /** A closed place while Cancelled and Duplicate are hidden: a thin column with its count, and no cards. */
  collapsed: boolean
}
/**
 * The board's columns, in order: the fixed states, and the project's own
 * columns each after the fixed column of its state, in the order the project
 * keeps them. Cancelled and Duplicate, and the columns of those types, show
 * whole only when asked; otherwise each is a thin column that counts its
 * issues and takes a drop.
 */
export function deriveColumns(issues: Iterable<Issue>, view: BoardView, ctx: FilterContext, now: number, own: readonly ProjectColumn[] = []): ColumnData[] {
  const states = [...BOARD_STATES, ...CLOSED_STATES]
  const matched = filterIssues(issues, view, ctx)
  const places = states.flatMap(state => [{ key: state, state, column: null }, ...own.filter(c => columnState(c) === state).map(column => ({ key: column.id, state, column }))])
  return places.map(({ key, state, column }) => {
    const all = sortIssues(matched.filter(i => placeOf(i, own) === key), view.sort)
    const terminal = state === 'done' || CLOSED_STATES.includes(state)
    const showAll = view.showAll.includes(key)
    const collapsed = !view.closed && CLOSED_STATES.includes(state)
    const items = collapsed ? [] : terminal && !showAll ? all.filter(i => inTerminalWindow(i, now)) : all
    return { key, state, column, items, total: all.length, hidden: all.length - items.length, terminal, showAll, collapsed }
  })
}
